// ════════════════════════════════════════════════════════════════════════════
// M2.7 Madurez de Procesos — Estudio de inversión TCO/ROI (Tanda C · 0744)
// Módulo económico del máster (Actividad 3): el ingeniero introduce costes
// (directos + indirectos por año) y beneficios anuales + curva de adopción; el
// panel computa EN VIVO el TCO total, el ROI horizonte = (Σbeneficios−TCO)/TCO×100,
// el ROI en régimen permanente, el payback y el cash-flow (chart recharts). Se
// persiste como `tcoRoi` en el análisis. Reutiliza el kit visual del BPMM.
// ════════════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { useI18n } from '../../../../i18n';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import { COLORS, SectionCard } from './components/analisis-ui-proceso';
import { guardarTcoRoiPemm, type TcoRoiPemm } from './api-pemm-proceso';

const VERDE = COLORS.verde;
const ROJO = COLORS.rojo;

const nf = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 });
const pf = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 1 });

function resize<T>(arr: T[], n: number, fill: T): T[] {
  const out = arr.slice(0, n);
  while (out.length < n) out.push(fill);
  return out;
}

const DEFECTO: TcoRoiPemm = {
  horizonteAnios: 5,
  costesDirectos: [0, 0, 0, 0, 0],
  costesIndirectos: [0, 0, 0, 0, 0],
  beneficios: [{ concepto: '', importeAnual: 0 }],
  curvaAdopcion: [0, 0.5, 1, 1, 1],
};

/** Cómputo puro del estudio TCO/ROI. */
export function computeTcoRoi(t: TcoRoiPemm) {
  const H = Math.max(1, t.horizonteAnios);
  const beneficioPleno = t.beneficios.reduce((s, b) => s + (Number(b.importeAnual) || 0), 0);
  const rows: { anio: string; coste: number; beneficio: number; neto: number; acum: number }[] = [];
  let acum = 0;
  let tco = 0;
  let sumBen = 0;
  for (let y = 0; y < H; y++) {
    const coste = (Number(t.costesDirectos[y]) || 0) + (Number(t.costesIndirectos[y]) || 0);
    const adop = t.curvaAdopcion[y] ?? (y === 0 ? 0 : 1);
    const beneficio = beneficioPleno * adop;
    const neto = beneficio - coste;
    acum += neto;
    tco += coste;
    sumBen += beneficio;
    rows.push({ anio: `Año ${y + 1}`, coste, beneficio, neto, acum });
  }
  const roiHorizonte = tco > 0 ? ((sumBen - tco) / tco) * 100 : 0;
  const costeRec = rows[H - 1]?.coste || 0;
  const roiPermanente = costeRec > 0 ? ((beneficioPleno - costeRec) / costeRec) * 100 : null;
  const paybackIdx = rows.findIndex((r) => r.acum >= 0);
  const payback = paybackIdx >= 0 ? paybackIdx + 1 : null;
  return { rows, tco, sumBen, beneficioPleno, roiHorizonte, roiPermanente, payback };
}

const inp: React.CSSProperties = {
  width: '100%',
  fontSize: 11.5,
  padding: '4px 6px',
  borderRadius: 4,
  border: `1px solid ${COLORS.gm}`,
  fontFamily: "'DM Sans',sans-serif",
  color: COLORS.a1,
  boxSizing: 'border-box',
  textAlign: 'right',
};

export function MadurezPemmTcoRoi({
  analisisId,
  initial,
  readOnly,
}: {
  analisisId: string;
  initial?: TcoRoiPemm | null;
  readOnly?: boolean;
}) {
  const { t } = useI18n();
  const k = ((t.process as Record<string, unknown>)?.madurezPemm ?? {}) as Record<string, string>;
  const tx = (key: string, fallback: string): string => k[key] ?? fallback;

  const [study, setStudy] = useState<TcoRoiPemm>(() => initial ?? DEFECTO);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const H = study.horizonteAnios;
  const setH = (n: number) => {
    const h = Math.max(1, Math.min(10, n));
    setStudy((s) => ({
      ...s,
      horizonteAnios: h,
      costesDirectos: resize(s.costesDirectos, h, 0),
      costesIndirectos: resize(s.costesIndirectos, h, 0),
      curvaAdopcion: resize(s.curvaAdopcion, h, 1),
    }));
    setSaved(false);
  };
  const setYear = (
    campo: 'costesDirectos' | 'costesIndirectos' | 'curvaAdopcion',
    y: number,
    v: number,
  ) => {
    setStudy((s) => {
      const arr = [...s[campo]];
      arr[y] = v;
      return { ...s, [campo]: arr };
    });
    setSaved(false);
  };
  const setBeneficio = (i: number, patch: Partial<{ concepto: string; importeAnual: number }>) => {
    setStudy((s) => {
      const b = [...s.beneficios];
      b[i] = { ...b[i], ...patch };
      return { ...s, beneficios: b };
    });
    setSaved(false);
  };
  const addBeneficio = () =>
    setStudy((s) => ({ ...s, beneficios: [...s.beneficios, { concepto: '', importeAnual: 0 }] }));
  const delBeneficio = (i: number) =>
    setStudy((s) => ({ ...s, beneficios: s.beneficios.filter((_, j) => j !== i) }));

  const guardar = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await guardarTcoRoiPemm(analisisId, study);
      setSaved(true);
    } catch {
      setError(tx('tcoError', 'No se pudo guardar el estudio (¿permiso de edición?)'));
    } finally {
      setSaving(false);
    }
  };

  const r = computeTcoRoi(study);
  const viable = r.roiHorizonte >= 0;

  const kpi = (label: string, value: string, color: string) => (
    <div style={{ background: COLORS.gc, borderRadius: 8, padding: '8px 12px', minWidth: 120 }}>
      <div
        style={{
          fontSize: 9,
          fontWeight: 700,
          color: COLORS.go,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
    </div>
  );

  const years = Array.from({ length: H }, (_, y) => y);

  return (
    <SectionCard title={tx('tcoTitulo', 'Estudio de inversión · TCO / ROI')} accent={COLORS.cyan}>
      {/* KPIs computados */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
        {kpi(tx('tcoTotal', 'TCO total'), nf.format(r.tco) + ' €', COLORS.a1)}
        {kpi(
          tx('roiHorizonte', 'ROI horizonte'),
          pf.format(r.roiHorizonte) + ' %',
          viable ? VERDE : ROJO,
        )}
        {kpi(
          tx('roiPermanente', 'ROI permanente'),
          r.roiPermanente == null ? '—' : pf.format(r.roiPermanente) + ' %',
          VERDE,
        )}
        {kpi(
          tx('payback', 'Payback'),
          r.payback == null ? tx('noRecuperado', 'No recuperado') : `Año ${r.payback}`,
          r.payback == null ? ROJO : COLORS.a1,
        )}
      </div>

      {/* Cash-flow chart */}
      <div style={{ width: '100%', height: 220, marginBottom: 12 }}>
        <ResponsiveContainer>
          <ComposedChart data={r.rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
            <XAxis dataKey="anio" tick={{ fontSize: 10, fill: COLORS.go }} />
            <YAxis
              tick={{ fontSize: 10, fill: COLORS.go }}
              tickFormatter={(v) => nf.format(v as number)}
            />
            <Tooltip formatter={(v) => nf.format(Number(v)) + ' €'} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <ReferenceLine y={0} stroke={COLORS.go} />
            <Bar dataKey="neto" name={tx('flujoNeto', 'Flujo neto')} fill={COLORS.a2} />
            <Line
              type="monotone"
              dataKey="acum"
              name={tx('flujoAcum', 'Flujo acumulado')}
              stroke={viable ? VERDE : ROJO}
              strokeWidth={2}
              dot
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Horizonte */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.a1 }}>
          {tx('horizonte', 'Horizonte (años)')}:
        </span>
        <input
          type="number"
          min={1}
          max={10}
          value={H}
          disabled={readOnly}
          onChange={(e) => setH(Number(e.target.value))}
          style={{ ...inp, width: 56, textAlign: 'center' }}
        />
      </div>

      {/* Tabla costes + curva por año */}
      <div style={{ overflowX: 'auto', marginBottom: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', color: COLORS.a1, padding: '4px 6px' }}></th>
              {years.map((y) => (
                <th key={y} style={{ color: COLORS.a1, padding: '4px 6px', fontSize: 10 }}>
                  Año {y + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(
              [
                ['costesDirectos', tx('costesDirectos', 'Costes directos (€)')],
                ['costesIndirectos', tx('costesIndirectos', 'Costes indirectos (€)')],
                ['curvaAdopcion', tx('curvaAdopcion', 'Curva de adopción (0-1)')],
              ] as const
            ).map(([campo, label]) => (
              <tr key={campo}>
                <th
                  style={{
                    textAlign: 'left',
                    color: COLORS.go,
                    fontWeight: 600,
                    padding: '3px 6px',
                  }}
                >
                  {label}
                </th>
                {years.map((y) => (
                  <td key={y} style={{ padding: '2px 3px' }}>
                    <input
                      type="number"
                      step={campo === 'curvaAdopcion' ? 0.1 : 10}
                      value={study[campo][y] ?? (campo === 'curvaAdopcion' ? 1 : 0)}
                      disabled={readOnly}
                      onChange={(e) => setYear(campo, y, Number(e.target.value))}
                      style={inp}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Beneficios */}
      <div style={{ marginBottom: 10 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            color: COLORS.a2,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            marginBottom: 4,
          }}
        >
          {tx('beneficios', 'Beneficios anuales (régimen permanente)')}
        </div>
        {study.beneficios.map((b, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'center' }}>
            <input
              type="text"
              value={b.concepto}
              disabled={readOnly}
              placeholder={tx('conceptoPh', 'Concepto (p.ej. reducción de reprocesos)')}
              onChange={(e) => setBeneficio(i, { concepto: e.target.value })}
              style={{ ...inp, textAlign: 'left', flex: 1 }}
            />
            <input
              type="number"
              step={10}
              value={b.importeAnual}
              disabled={readOnly}
              onChange={(e) => setBeneficio(i, { importeAnual: Number(e.target.value) })}
              style={{ ...inp, width: 110 }}
            />
            {!readOnly && (
              <button
                onClick={() => delBeneficio(i)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: ROJO,
                  cursor: 'pointer',
                  fontSize: 14,
                }}
                title="Eliminar"
              >
                ×
              </button>
            )}
          </div>
        ))}
        {!readOnly && (
          <button
            onClick={addBeneficio}
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: COLORS.a2,
              background: 'transparent',
              border: `1px dashed ${COLORS.gm}`,
              borderRadius: 6,
              padding: '4px 10px',
              cursor: 'pointer',
            }}
          >
            {tx('addBeneficio', '+ Añadir beneficio')}
          </button>
        )}
      </div>

      {error && (
        <div style={{ color: ROJO, fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{error}</div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {!readOnly && (
          <button
            onClick={guardar}
            disabled={saving}
            style={{
              fontSize: 12,
              fontWeight: 700,
              padding: '6px 14px',
              borderRadius: 6,
              border: 'none',
              background: COLORS.a1,
              color: COLORS.w,
              cursor: 'pointer',
              opacity: saving ? 0.6 : 1,
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            {saving ? tx('guardando', 'Guardando…') : tx('tcoGuardar', 'Guardar estudio')}
          </button>
        )}
        {saved && (
          <span style={{ fontSize: 11, fontWeight: 700, color: VERDE }}>
            {tx('guardado', '✓ Guardado')}
          </span>
        )}
        <span style={{ fontSize: 11, color: COLORS.go, marginLeft: 'auto' }}>
          {tx(
            'tcoNota',
            'ROI horizonte = (Σ beneficios − TCO) / TCO × 100. Beneficio del año = pleno × curva de adopción.',
          )}
        </span>
      </div>
    </SectionCard>
  );
}
